import moment, { type Moment } from "moment";
import * as ethers from "ethers";
import { getAccount, } from "wagmi/actions";
// import { formatUnits, type BigNumberish, } from "ethers/utils";
import { executableTokens, swappableTokens, CHAINED_SYMBOLS, VALID_CHAINS, } from "@const";
import { Addresses, wagmiConfig, } from "@config";
import { isAddress, } from "@types";
import { getChainId, } from "./getChainId";

export * from "./getChainId";
export * from "./getChainTokenLabels";
export * from "./getTimedImageLoc";

import type { ExecutableToken, Address, Token, SwappableToken, SafaryEventProps, ChainId, NumericString, } from "@types";
// export { parseEther, type BigNumberish, } from "ethers/utils";

type BigNumberish = any;

/** get the value of a bigint
 * @summary - BigInt / 1e18; 
 * @returns {Number} */
export const n = ( value: BigInt ) => Number(value) / 1e18;

/** return numeric value as bigint
 * @param { number | string } value number, float or string representation of the value
 * @summary floors value * 1e18 then returns as bigint
 * @returns {BigInt} */
export const bi = ( value: number | string ): bigint => {
	try {
		value = typeof value === "string" && value.endsWith(".") ? `${value}0` : value;
		const valueNumber = typeof value === "number" ? value : parseFloat(value);
		return BigInt(Math.floor(valueNumber * 1e18));
	}
	catch( err ) {
		throw new Error(`failed converting to bigint value "${value.toString()}"`);
	}
}

/** consume a numeric string, and ensure it will be usable as a number, eg. if empty , make zero, if ends with . remove the decimal point */
export const sn = ( value: NumericString, ) => {

	if( !value.length ) return "0";
	if( value === "." ) return "0";
	if( value.endsWith(".") ) return `${value.substring(0, value.length-1)}`;
	return value;
}

/** @deprecated */
export const eth2wei = ( eth: BigInt | number | string ): BigInt => {
	console.log("eth2wei is being deprecated");
	/** @ts-ignore */
	try { return ethers.utils.parseEther(parseFloat(eth.toString()).toFixed(8) + "0".repeat(10)); }
	catch( err ) { return BigInt(0); }
}

/** @deprecated */
export const weiToEth = ( wei: BigInt | number | string ): string => {
	console.log("weiToEth is being deprecated");
	return ethers.utils.formatUnits(wei as BigNumberish, "ether");
}

export const raise = ( message: string ): never => {
	throw new Error(message);
}


/**
 * @deprecated - use configuration address book (config.Addresses)
 */
export const tokenToAddress = new Map<ExecutableToken, Address>([
	[ "wstETH", "0xC7434BE51B72bf9F75D5A8884f2Fb06C58151793" ],
	[ "rETH", "0xbf7bC915ad9f6c9c437CA77AB8530a77CaC9e3C4" ],
	[ "eETH", "0xC7434BE51B72bf9F75D5A8884f2Fb06C58151793" ],
	[ "ezETH", "0xE48F5D61bEC05dc879652ed04c5cA5Dc03BCEf24" ],
]);


export const isExecutableToken = ( value: Token ): value is ExecutableToken => {
	return typeof value === "string" && executableTokens.includes(value as ExecutableToken);
}

export const isSwappableToken = ( value: Token ): value is SwappableToken => {
	return typeof value === "string" && swappableTokens.includes(value as SwappableToken);
}



/**
 * get the address for a given token
 * @param {ExecutableToken} token the token
 * @returns {Address} the qualified address for the argued token
 */
export const tokenAddress = ( token: Token, ): Address | undefined => {
	if( token === "ETH" ) return undefined;
	if( token in Addresses ) {
		return Addresses[token as keyof typeof Addresses] as Address;
	}
	throw new Error(`tokenAddress: token "${token}" does not have an address in the configured addressbook`);
}


export const isUserRejectionTxn = ( error: any ): boolean => {
	try { return error.cause.code === 4001; } catch ( err ) { return false; }
}


export const assertError = ( value: unknown ): value is Error => {
	if( value && value instanceof Error ) return true;
	throw new Error("assertError: received a value which is NOT an error.");
}


export const isValidChain = ( value: unknown ): value is ChainId => {
	return typeof value === "number" && VALID_CHAINS.includes(value as ChainId);
}


export const getConnectionInfo = ( ): { chain: ChainId, address: Address, } => {
	const chainId = getChainId();
	const { address, } = getAccount(wagmiConfig);
	if( chainId && isValidChain(chainId) ) {
		if( isAddress(address) ) {
			return { chain: chainId, address, };
		}
		throw new Error(`EscrowStore::getConnectionInfo - wagmi failed to produce a valid wallet address - [${address}]`, );
	}
	throw new Error(`EscrowStore::getConnectionInfo - wagmi failed to produce a valid chain - [${chainId}]`);
}


export const getConnectionInfoMaybe = ( ): { chain: ChainId | null, address: Address | null, } => {
	const chainId = getChainId();
	const { address, } = getAccount(wagmiConfig);
	if( chainId && isValidChain(chainId) ) {
		if( isAddress(address) ) {
			return { chain: chainId, address, };
		}
		return { chain: chainId, address: null, };
	}
	return { chain: null, address: null, };
}


// export const getChainId = (): ChainId => {
// 	try {
// 		const { chain, } = getConnectionInfo();
// 		return chain;
// 	}
// 	catch( err ) {
// 		throw new Error(`@stake-app/helpers::getChainId failed obtaining a valid chain id - ${err instanceof Error ? err.message : "no error instance"}`);
// 	}
// }


export const getBlockExplorerLink = ( chain?: ChainId, ) => {
	chain = chain || getChainId();
	switch( chain ) {
		case 1: return "https://etherscan.io/";
		case 42161:
		case 4216188:
			return "https://arbiscan.io/";
		default:
			throw new Error(`@stake-app/helpers::getBlockExplorerLink received unknown chain id ${chain}`);
	}
}


export const getWalletAddress = (): Address => {
	try {
		const { address, } = getConnectionInfo();
		return address;
	}
	catch( err ) {
		throw new Error(`@stake-app/helpers::getWalletAddress failed obtaining a valid wallet id - ${err instanceof Error ? err.message : "no error instance"}`);
	}
}


export const getBlockLink = ( address: Address, ): { link: string, text: string }  => {
	try {
		const { chain, } = getConnectionInfo();
		if( isAddress(address) ) {
			switch( chain ) {
				case 1: return { link: `https://etherscan.io/tx/${address}`, text: "See on etherscan" };
				case 18: return { link: `https://dashboard.tenderly.co/explorer/vnet/c5854785-21b0-4f30-b807-e5d20cd31aeb/transactions/${address}`, text: "See on Tenderley 8" };
				case 188: return { link: `https://etherscan.io/tx/${address}`, text: "See on etherscan" };
				case 42161: return { link: `https://arbiscan.io/tx/${address}`, text: "See on arbiscan" };
				case 421618: return { link: `https://dashboard.tenderly.co/explorer/vnet/2f9c40a3-a0fd-4764-9eff-77efced4c879/transactions/${address}`, text: "See on arbiscan" };
				case 4216188: return { link: `https://arbiscan.io/tx/${address}`, text: "See on arbiscan" };
				default:
					throw new Error(`unknown chain identifier ${chain}`)
			}
		}
		throw new Error(`not a valid address "${address}"`);
	}
	catch( err ) {
		throw new Error(`@bracket/stake-app::getBlockLink failed to obtain a blockchain entry ${err instanceof Error ? err.message : "no error instance"}`);
	}
}


/** return exact balance if the amount is slightly higher after rounding */
export const roundToBalance = ( amount: bigint, balance: bigint ): bigint => {
	// console.log("AMT", amount, n(amount));
	// console.log("BAL", balance, n(balance));
	// console.log("DIFF", bi(0.0001), n(bi(0.0001)));
	// (window as any).a = amount;
	// (window as any).b = balance;
	if( amount > balance ) {
		if( amount - bi(0.0001) <= balance ) {
			return balance ;
		}
		// console.log("BAD")
	}
	// console.log("ISNT GG");
	// console.log("RET AMOUNT");
	return amount;
}


export const pointsToPercent = ( points: number, ) => {
	if( typeof points === "number" ) {
		return `${points * 100}%`;
	}
	return "hi"
}


export const formattedPoints = ( points: number, ) => {
	return Intl.NumberFormat("en-us").format(Math.round(points));
}
